/**
 * Calendar API endpoints - Google, Microsoft, Subscribed calendars
 */
import { fetchApi } from './apiBase';

// === Types ===

export interface GoogleCalendar {
  id: number;
  name: string;
  google_calendar_id: string;
  color: string;
  is_primary: boolean;
  is_enabled: boolean;
  last_synced_at: string | null;
  last_error?: string | null;
  /** Sent by CalendarSerializer. 'paused' = revoked or failing access; the
   *  sync stopped and needs the person. */
  sync_status?: 'ok' | 'paused' | 'disabled' | string;
  /** Why it paused: 'auth' (the account's access was lost — one problem for
   *  every calendar), 'permission' or 'not_found' (this calendar's own). */
  pause_reason?: string | null;
}

export interface MicrosoftCalendar {
  id: number;
  name: string;
  microsoft_calendar_id: string;
  color: string;
  is_default: boolean;
  is_enabled: boolean;
  can_edit: boolean;
  last_synced_at: string | null;
  family_member: number | null;
  family_member_name: string | null;
  auto_assign_members: boolean;
}

export interface MicrosoftConnectionStatus {
  connected: boolean;
  has_token: boolean;
  token_expires_at: string | null;
  calendars_count: number;
}

export interface SubscribedCalendar {
  id: string;
  name: string;
  url: string;
  color: string;
  is_enabled: boolean;
  is_school_calendar?: boolean;
  family_member: number | null;
  family_member_name: string | null;
  last_synced_at: string | null;
  event_count: number;
  /** Person-readable failure reason. (The old `sync_error` name was a
   *  phantom — the API has always sent `last_error`.) */
  last_error: string;
  /** 'paused' = syncing stopped and needs the person (dead feed). */
  sync_status?: 'ok' | 'paused' | string;
}

export interface CalendarSyncResult {
  calendar_id: string;
  calendar_name: string;
  success: boolean;
  events_synced?: number;
  error?: string;
}

export interface SubscribedCalendarEvent {
  id: string;
  calendar: string;
  calendar_name: string;
  calendar_color: string;
  family_member_id: number | null;
  family_member_name: string | null;
  uid: string;
  title: string;
  description: string;
  location: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  created_at: string;
  updated_at: string;
}

// === Google Calendar ===

export async function getGoogleCalendars(): Promise<GoogleCalendar[]> {
  return fetchApi<GoogleCalendar[]>('/api/calendars/calendars/');
}

export async function syncGoogleCalendars(): Promise<{ message: string; calendars: GoogleCalendar[] }> {
  return fetchApi('/api/calendars/calendars/sync/', { method: 'POST' });
}

export async function syncGoogleEvents(): Promise<{ message: string; count: number }> {
  return fetchApi('/api/calendars/google-events/sync/', { method: 'POST' });
}

export async function updateCalendarSettings(
  calendarId: number,
  data: { is_enabled: boolean }
): Promise<GoogleCalendar> {
  return fetchApi<GoogleCalendar>(`/api/calendars/calendars/${calendarId}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// === Microsoft Calendar ===

export async function getMicrosoftCalendars(): Promise<MicrosoftCalendar[]> {
  return fetchApi<MicrosoftCalendar[]>('/api/calendars/microsoft/');
}

export async function syncMicrosoftCalendars(): Promise<{ message: string; calendars: MicrosoftCalendar[] }> {
  return fetchApi('/api/calendars/microsoft/sync/', { method: 'POST' });
}

export async function getMicrosoftConnectionStatus(): Promise<MicrosoftConnectionStatus> {
  return fetchApi<MicrosoftConnectionStatus>('/api/calendars/microsoft/status/');
}

export async function updateMicrosoftCalendarSettings(
  calendarId: number,
  data: { is_enabled?: boolean; family_member?: number | null; auto_assign_members?: boolean }
): Promise<MicrosoftCalendar> {
  return fetchApi<MicrosoftCalendar>(`/api/calendars/microsoft/${calendarId}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// === Subscribed (ICS) Calendars ===

export async function getSubscribedCalendars(): Promise<SubscribedCalendar[]> {
  return fetchApi<SubscribedCalendar[]>('/api/calendars/subscribed/');
}

export async function addSubscribedCalendar(
  name: string,
  url: string,
  color?: string,
  familyMemberId?: number
): Promise<SubscribedCalendar> {
  return fetchApi<SubscribedCalendar>('/api/calendars/subscribed/', {
    method: 'POST',
    body: JSON.stringify({
      name,
      url,
      color,
      family_member: familyMemberId,
    }),
  });
}

export async function updateSubscribedCalendar(
  calendarId: string,
  data: Partial<SubscribedCalendar>
): Promise<SubscribedCalendar> {
  return fetchApi<SubscribedCalendar>(`/api/calendars/subscribed/${calendarId}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteSubscribedCalendar(calendarId: string): Promise<void> {
  await fetchApi(`/api/calendars/subscribed/${calendarId}/`, {
    method: 'DELETE',
  });
}

export async function syncSubscribedCalendar(calendarId: string): Promise<{ message: string; calendar: SubscribedCalendar }> {
  return fetchApi(`/api/calendars/subscribed/${calendarId}/sync/`, {
    method: 'POST',
  });
}

export async function syncAllSubscribedCalendars(): Promise<{ message: string; results: CalendarSyncResult[] }> {
  return fetchApi('/api/calendars/subscribed/sync-all/', {
    method: 'POST',
  });
}

export async function reprocessCalendarAssignments(calendarId: string): Promise<{
  message: string;
  assigned: number;
  total: number;
  calendar: SubscribedCalendar;
}> {
  return fetchApi(`/api/calendars/subscribed/${calendarId}/reprocess-assignments/`, {
    method: 'POST',
  });
}

export async function getAllSubscribedEvents(
  start?: string,
  end?: string
): Promise<{ events: SubscribedCalendarEvent[] }> {
  const params = new URLSearchParams();
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  const query = params.toString() ? `?${params.toString()}` : '';
  return fetchApi<{ events: SubscribedCalendarEvent[] }>(`/api/calendars/subscribed/events/${query}`);
}
